// Dictation engine factory, chosen by a build-time flag:
//
//   REACT_APP_DICTATION_PROVIDER = 'deepgram' (default) | 'webspeech' | 'off'
//
//   • deepgram  — nova-2 streaming via a short-lived token from the
//                 getDeepgramToken function. Works everywhere, including the
//                 iOS app. Needs the DEEPGRAM_API_KEY secret, and a signed
//                 Deepgram BAA before real patient (PHI) audio goes through it.
//   • webspeech — the browser's built-in engine (Chrome/Edge/Android; dead in
//                 iOS WKWebView). Sends audio to Google with NO BAA — never use
//                 with PHI. Kept only as a fallback for demos.
//   • off       — no cloud STT; type-only. The UI degrades gracefully.

import { DictationCallbacks, DictationEngine, DictationProvider } from './types';
import { WebSpeechDictationEngine } from './WebSpeechDictationEngine';
import { DeepgramDictationEngine } from './DeepgramDictationEngine';

export type { DictationEngine, DictationCallbacks, DictationProvider, DictationStatus } from './types';

export function resolveDictationProvider(): DictationProvider {
  const raw = (process.env.REACT_APP_DICTATION_PROVIDER || 'deepgram').toLowerCase();
  if (raw === 'webspeech' || raw === 'off') return raw;
  return 'deepgram';
}

/**
 * Build the active dictation engine, or `null` when the provider is 'off'
 * (type-only mode). Callers should handle null by allowing manual typing.
 */
export function createDictationEngine(callbacks: DictationCallbacks): DictationEngine | null {
  switch (resolveDictationProvider()) {
    case 'deepgram':
      return new DeepgramDictationEngine(callbacks);
    case 'off':
      return null;
    case 'webspeech':
    default:
      return new WebSpeechDictationEngine(callbacks);
  }
}
